import { Box, Text } from '@user-portal/wikr-components';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectCancellationCandidates } from 'store/subscriptions/selectors';

import { sendAnalyticCancelOfferClick, trackScreenLoadCancelOffer } from 'services/Analytics';

import useModals from 'modals/hooks';
import { ModalName } from 'modals/types';

import { getValuesByKey } from 'helpers/utils';

import { CustomPrimaryButton } from 'components/Buttons';
import OutlinedButton from 'components/Buttons/OutlinedButton';
import Modal from 'components/Modal';

import { CancelFlowOffers, CancelOfferAction } from 'types/subscription';

import useCancelSubscriptionFlow from '../../../hooks/useCancelSubscriptionFlow';
import FullScreenModalHeader from '../../components/FullScreenModalHeader';

import Steps from './components/Steps';

const ReminderModal = () => {
    const { closeModal, openModal } = useModals();
    const { t } = useTranslation();

    const { onCancelSubscription, onRequestSetReminder } = useCancelSubscriptionFlow();

    const cancellationCandidates = useSelector(selectCancellationCandidates);

    const subscription_id = useMemo(
        () => getValuesByKey(cancellationCandidates, 'external_id'),
        [cancellationCandidates]
    );

    const sendAnalytics = useCallback(
        (action: CancelOfferAction) => {
            sendAnalyticCancelOfferClick({
                eventLabel: { subscription_id, offer: CancelFlowOffers.Reminder },
                actionEvent: action,
            });
        },
        [subscription_id]
    );

    useEffect(() => {
        trackScreenLoadCancelOffer({
            eventLabel: { subscription_id, offer: CancelFlowOffers.Reminder },
        });
    }, [subscription_id]);

    const onBackButtonClick = () => {
        sendAnalytics(CancelOfferAction.Close);

        openModal(ModalName.CancellationReasonModal);
    };

    const onClose = useCallback(() => {
        sendAnalytics(CancelOfferAction.Close);

        closeModal();
    }, [closeModal, sendAnalytics]);

    const onCancel = () => {
        sendAnalytics(CancelOfferAction.Cancel);

        onCancelSubscription();
    };

    const onSubmit = () => {
        sendAnalytics(CancelOfferAction.Accept);

        onRequestSetReminder();
    };

    return (
        <Modal
            fullscreen
            isOpen
            onClose={onClose}
            customStyles={{ padding: 0 }}
            withRoundedCorners={false}
            dataTestId="ReminderModalControl"
        >
            <FullScreenModalHeader onBackButtonClick={onBackButtonClick} closeModal={onClose} />
            <Box $paddingTop={32} $paddingBottom={32} className="container-sm">
                <Text medium center mb={16} type="h4">
                    {t('subscription.reminderModal.title')}
                </Text>
                <Text mb={34} center type="large-text" color="text-secondary">
                    {t('subscription.reminderModal.subtitle')}
                </Text>
                <Steps />

                <CustomPrimaryButton mb={16} onClick={onSubmit} dataLocator="setReminderButton">
                    {t('subscription.reminderModal.remindBtn')}
                </CustomPrimaryButton>
                <OutlinedButton
                    onClick={onCancel}
                    text={t('basics.button.cancelSubscription')}
                    backgroundColor="surface-main"
                    dataLocator="cancelSubscriptionButton"
                />
            </Box>
        </Modal>
    );
};

export default ReminderModal;
